// データ取込画面（中身/TMS）。CSVを選ぶ → プレビューで ①重複/②内容変更/新規 を確認 → 確定取込。
// 出所: 要件「1 データ取込（配車8ファイル/トランコム/ハコベル/デジタコ）→ 2 データ照合（①重複除外 ②内容変更）」。
import { VENDOR_LIST, VENDORS } from '../data/ingest/vendors.js'
import { decodeBytes } from '../data/ingest/csv.js'
import { planImport } from '../data/ingest/plan.js'
import { buildPlan, commitPlan } from '../data/ingest/importRun.js'
import { seedMasters } from '../data/masters/seedMasters.js'
import { supabaseReady } from '../supabase.js'
import { getOne, listDocs, upsert } from '../data/collections.js'

// 取込時の安全網ctx（日計表のみ）: 移行済担当の切替日＋既存ハコベル便の案件番号/署名。
async function loadImportCtx(vId, online) {
  const ctx = { year: new Date().getFullYear() }
  if (!online || vId !== 'daily') return ctx
  try { const mig = await getOne('settings', 'migration'); ctx.migratedStaff = (mig && mig.staff) || {} } catch (e) { ctx.migratedStaff = {} }
  try {
    const trips = await listDocs('trips')
    const hako = trips.filter((t) => t.source === 'hacobell' || t.source === 'hakobel-july')
    const ank = (t) => t.ankenNo || (t.source === 'hakobel-july' ? String(t.id).replace(/^HKB-/, '') : String(t.id).replace(/^hacobell__/, ''))
    ctx.hakoAnken = new Set(hako.map(ank).filter(Boolean))
    ctx.hakoSig = new Set(hako.map((t) => `${t.date || ''}|${String(t.driverName || '').replace(/\s+/g, '')}|${Math.round(Number(t.fare) || 0)}`))
    // VEIL直接入力（手入力/定期便）の署名＝同一便を日計表から二重取込しない（管理IDが別体系でも吸収）。
    //  照合の当事者＝自社便は乗務員／傭車便は協力会社（傭車は自社乗務員が空のため協力会社で照合）。
    const veilParty = (t) => String(t.driverName || '').trim() || String(t.partnerName || '').trim()
    const veil = trips.filter((t) => t.source === 'manual' || t.source === 'teiki')
    ctx.veilSig = new Set(veil
      .filter((t) => t.date && Number(t.fare) > 0 && t.kind !== 'other' && veilParty(t))
      .map((t) => `${t.date}|${veilParty(t).replace(/\s+/g, '')}|${Math.round(Number(t.fare))}`))
  } catch (e) { ctx.hakoAnken = new Set(); ctx.hakoSig = new Set(); ctx.veilSig = new Set() }
  return ctx
}

// 重複チェック（常設バックストップ）: 同一管理ID(base=idの__N除去)＋内容一致 の余剰＝重複 を検出。
async function checkDuplicates(el, btn) {
  const box = el.querySelector('[data-dupresult]')
  const old = btn.textContent
  btn.disabled = true; btn.textContent = 'チェック中…'
  try {
    const trips = await listDocs('trips')
    const base = (id) => String(id).replace(/__\d+$/, '')
    const sig = (t) => [t.date || '', String(t.driverName || '').replace(/\s+/g, ''), Math.round(Number(t.fare) || 0), t.customerName || '', t.origin || '', t.dest || ''].join('|')
    const groups = {}
    for (const t of trips) { const k = base(t.id) + '' + sig(t); (groups[k] = groups[k] || []).push(t.id) }
    let excess = 0; const samples = []
    for (const k in groups) { const ids = groups[k]; if (ids.length > 1) { excess += ids.length - 1; if (samples.length < 6) { ids.sort(); samples.push(ids.join(' , ')) } } }
    if (box) box.innerHTML = excess === 0
      ? `<span class="pill2 s">重複なし ✓</span> <span class="ing-note">総 ${trips.length} 件・同一管理ID＋内容一致の余剰 0</span>`
      : `<span class="pill2 e">重複 ${excess} 件</span> <span class="ing-note">余剰行あり（同一管理ID＋内容一致）。例: ${samples.map(esc).join(' ／ ')}</span>`
  } catch (e) {
    if (box) box.innerHTML = `<span class="ing-note">チェック失敗: ${esc(e && e.message || e)}</span>`
  }
  btn.disabled = false; btn.textContent = old
}

const YEN = (n) => '¥' + Number(n || 0).toLocaleString('ja-JP')
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const STATUS = {
  new: { label: '新規', cls: 'is-new' },
  changed: { label: '内容変更', cls: 'is-chg' },
  unchanged: { label: '重複(スキップ)', cls: 'is-dup' },
  error: { label: 'エラー', cls: 'is-err' },
  skipped: { label: 'スキップ', cls: 'is-skip' },
}
// 4ソース（配車日計/トランコム/ハコベル/デジタコ）はすべて VENDOR_LIST に実装済み。
const SOON = []

const state = {} // vendorId -> { fileName, text, plan, busy }

function styleOnce() {
  if (document.getElementById('ing-style')) return
  const s = document.createElement('style')
  s.id = 'ing-style'
  s.textContent = `
  .ing-head{margin:2px 0 14px}
  .ing-head h2{margin:0 0 4px;font-size:18px}
  .ing-head p{margin:0;color:var(--text);font-size:12.5px;line-height:1.6}
  .ing-flow{display:inline-flex;gap:6px;margin-top:8px;font-size:11.5px;color:var(--text)}
  .ing-flow span{background:var(--head);border:1px solid var(--e1);border-radius:999px;padding:2px 9px}
  .ing-banner{margin:10px 0 6px;padding:9px 12px;border-radius:9px;font-size:12px;background:var(--card);border:1px solid var(--accent);color:var(--signal)}
  .ing-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:14px}
  .ing-card{background:var(--card);border:1px solid var(--e1);border-radius:12px;padding:14px 15px}
  .ing-card h3{margin:0 0 2px;font-size:14px;display:flex;align-items:center;gap:8px}
  .ing-card .k{color:var(--muted);font-size:11px;font-weight:400}
  .ing-card .desc{color:var(--text);font-size:11.5px;margin:2px 0 10px;line-height:1.5}
  .ing-file{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
  .ing-file input[type=file]{font-size:12px}
  .ing-fn{font-size:11.5px;color:var(--text)}
  .ing-sum{display:flex;gap:7px;flex-wrap:wrap;margin:11px 0 4px}
  .pill2{font-size:11.5px;border-radius:999px;padding:3px 10px;font-weight:700;border:1px solid transparent}
  .pill2.n{background:var(--head);color:var(--text);border-color:var(--e1)}
  .pill2.c{background:transparent;color:var(--signal);border-color:var(--accent)}
  .pill2.d{background:var(--head);color:var(--text);border-color:var(--e1)}
  .pill2.e{background:var(--head);color:var(--neg);border-color:var(--e1)}
  .pill2.s{background:var(--head);color:var(--pos);border-color:var(--e1)}
  .ing-actions{display:flex;gap:8px;align-items:center;margin-top:6px}
  .ing-btn{border:0;border-radius:8px;padding:8px 14px;font-size:12.5px;font-weight:700;cursor:pointer}
  .ing-btn.primary{background:var(--head);color:var(--text)}
  .ing-btn.primary:disabled{background:var(--head);cursor:not-allowed}
  .ing-btn.ghost{background:var(--head);color:var(--text)}
  .ing-note{font-size:11px;color:var(--muted)}
  .ing-tblwrap{margin-top:10px;max-height:280px;overflow:auto;border:1px solid var(--e1);border-radius:8px}
  table.ing-tbl{width:100%;border-collapse:collapse;font-size:11.5px}
  .ing-tbl th,.ing-tbl td{padding:5px 8px;border-bottom:1px solid var(--e1);text-align:left;white-space:nowrap}
  .ing-tbl th{position:sticky;top:0;background:var(--head);color:var(--text);font-weight:700;z-index:1}
  .ing-tbl td.num{text-align:right;font-variant-numeric:tabular-nums}
  .badge{font-size:10.5px;font-weight:700;border-radius:5px;padding:1px 6px}
  .badge.is-new{background:var(--head);color:var(--text)}
  .badge.is-chg{background:transparent;color:var(--signal)}
  .badge.is-dup{background:var(--head);color:var(--text)}
  .badge.is-err{background:var(--head);color:var(--neg)}
  .badge.is-skip{background:var(--head);color:var(--pos)}
  .diff{color:var(--signal);font-size:10.5px}
  .badge.is-warn{background:transparent;color:var(--signal)}
  .pill2.w{background:transparent;color:var(--signal);border-color:var(--accent)}
  .warn{color:var(--signal);font-size:10.5px;margin-top:2px}
  .ing-mig{background:var(--card);border:1px solid var(--e1);border-radius:12px;padding:12px 14px;margin-top:12px}
  .ing-mig h3{margin:0 0 6px;font-size:13.5px;display:flex;gap:8px;align-items:baseline}
  .ing-mig h3 .k{color:var(--muted);font-size:11px;font-weight:400}
  .ing-mig .mrow{display:flex;align-items:center;gap:10px;font-size:12px;padding:4px 0;border-bottom:1px solid var(--e3)}
  .ing-mig .mrow b{min-width:90px}
  .ing-migadd{display:flex;gap:6px;flex-wrap:wrap;margin-top:8px}
  .ing-migadd input{font-size:12px;padding:5px 8px;border:1px solid var(--e1);border-radius:6px}
  .ing-soon{opacity:.72}
  `
  document.head.appendChild(s)
}

function summaryHtml(plan) {
  const s = plan.summary
  const parts = [
    `<span class="pill2 n">新規 ${s.new}</span>`,
    `<span class="pill2 c">内容変更 ${s.changed}</span>`,
    `<span class="pill2 d">重複スキップ ${s.unchanged}</span>`,
  ]
  if (s.skipped) parts.push(`<span class="pill2 s">スキップ ${s.skipped}</span>`)
  if (s.warn) parts.push(`<span class="pill2 w">要確認 ${s.warn}</span>`)
  if (s.error) parts.push(`<span class="pill2 e">エラー ${s.error}</span>`)
  if (s.intraDup) parts.push(`<span class="ing-note">（同一ファイル内の重複 ${s.intraDup} 件は最新行を採用）</span>`)
  return parts.join('')
}

function rowsTable(plan) {
  const shown = plan.rows.filter((r) => r.status !== 'unchanged' || r.warn).slice(0, 60)
  const dupCount = plan.rows.filter((r) => r.status === 'unchanged').length
  const isTacho = (VENDORS[plan.vendorId]?.target) === 'tachographs'
  const km = (n) => (n || n === 0 ? Number(n).toLocaleString('ja-JP') + 'km' : '—')
  const body = shown.map((r) => {
    const t = r.trip || {}
    const st = STATUS[r.status] || STATUS.error
    const diff = r.status === 'changed' && r.diff && r.diff.length
      ? `<div class="diff">${r.diff.map((d) => `${esc(d.field)}: ${esc(d.from)}→${esc(d.to)}`).join(' / ')}</div>`
      : (r.status === 'skipped' ? `<div class="diff">${esc(r.reason || ('管理NO ' + (t.ankenNo || '') + '｜ハコベルCSV由来のため日計表からは取り込みません'))}</div>` : '')
    const warnHtml = r.warn ? `<div class="warn">⚠ ${esc(r.warn)}</div>` : ''
    if (r.status === 'error') {
      return `<tr><td><span class="badge is-err">エラー</span></td><td colspan="6">行${r.row}: ${esc(r.error)}</td></tr>`
    }
    if (isTacho) {
      return `<tr>
        <td><span class="badge ${st.cls}">${st.label}</span></td>
        <td>${esc(t.date)}</td>
        <td>${esc(t.vehicleName || t.plate)}</td>
        <td>${esc(t.driverName)}</td>
        <td class="num">${km(t.runKm)}</td>
        <td class="num">${km(t.loadedKm)}</td>
        <td class="num">${t.fuelLiter ? Number(t.fuelLiter).toLocaleString('ja-JP') + 'L' : '—'}${diff}</td>
      </tr>`
    }
    return `<tr>
      <td><span class="badge ${st.cls}">${st.label}</span>${r.warn ? ' <span class="badge is-warn">要確認</span>' : ''}</td>
      <td>${esc(t.date)}</td>
      <td>${esc(t.customerName)}${t.kind === 'charter' ? ' <span class="ing-note">傭</span>' : ''}</td>
      <td>${esc(t.origin)}→${esc(t.dest)}</td>
      <td class="num">${YEN(t.fare)}</td>
      <td class="num">${t.hasToll ? YEN(t.tollCost) : '—'}</td>
      <td>${esc(t.driverName || t.partnerName)}${diff}${warnHtml}</td>
    </tr>`
  }).join('')
  const more = shown.length < plan.rows.filter((r) => r.status !== 'unchanged').length
    ? `<tr><td colspan="7" class="ing-note">…ほか（新規/変更/スキップのみ先頭60件表示）</td></tr>` : ''
  const dupNote = dupCount ? `<tr><td colspan="7" class="ing-note">重複スキップ ${dupCount} 件は非表示（①既存と同一内容）</td></tr>` : ''
  return `<div class="ing-tblwrap"><table class="ing-tbl">
    <thead><tr>${isTacho ? '<th>判定</th><th>運行日</th><th>車番</th><th>乗務員</th><th>走行</th><th>実車</th><th>給油</th>' : '<th>判定</th><th>運行日</th><th>得意先</th><th>発→着</th><th>運賃</th><th>高速</th><th>乗務員/協力</th>'}</tr></thead>
    <tbody>${body || '<tr><td colspan=7 class="ing-note">対象行なし</td></tr>'}${more}${dupNote}</tbody>
  </table></div>`
}

export function renderIngest(el, ctx = {}) {
  styleOnce()
  const demo = !!ctx.demo
  const online = !demo && supabaseReady

  const cards = VENDOR_LIST.map((v) => {
    const s = state[v.id]
    return `<div class="ing-card" data-v="${v.id}">
      <h3>${esc(v.label)} <span class="k">${v.id === 'daily' ? '管理ID/複合キー' : (v.idColumn ? v.idColumn + 'キー' : '複合キー')}・${v.encoding === 'shift_jis' ? 'Shift_JIS' : 'UTF-8'}${v.target === 'tachographs' ? '・実車率' : ''}</span></h3>
      <div class="desc">CSVを選ぶと自動でプレビュー。①既存と同一＝重複スキップ／②運賃・高速代など変化＝内容変更。</div>
      <div class="ing-file">
        <input type="file" accept=".csv,text/csv" data-file="${v.id}">
        <span class="ing-fn" data-fn="${v.id}">${s?.fileName ? esc(s.fileName) : ''}</span>
      </div>
      <div data-preview="${v.id}">${s?.plan ? summaryBlock(s.plan, v.id, online) : ''}</div>
    </div>`
  }).join('')

  const soon = SOON.map((x) => `<div class="ing-card ing-soon">
      <h3>${esc(x.label)} <span class="k">準備中</span></h3>
      <div class="desc">${esc(x.note)}</div>
    </div>`).join('')

  el.innerHTML = `
    <div class="ing-head">
      <h2>データ取込</h2>
      <p>配車担当の日計表・トランコム・ハコベル・デジタコのCSVを取り込み、運行（trips）／デジタコ実績（tachographs）に反映します。同じ内容の再取込は重複として除外し、高速代・走行kmなどの変更だけを内容変更として更新します。取込時に相手先は正式名称（コード＋枝番）へ名寄せされます。</p>
      <div class="ing-flow"><span>1. CSV選択</span><span>2. プレビュー（①②照合）</span><span>3. 確定取込</span></div>
      ${demo ? '<div class="ing-banner">デモ表示（Supabase未接続）：プレビューまで動作します。確定取込・マスタ投入は本番接続時に有効化されます。</div>'
             : (!supabaseReady ? '<div class="ing-banner">Supabase未設定のため確定取込は無効です。</div>' : '')}
      <div class="ing-seed"><button class="ing-btn ghost" data-seed ${online ? '' : 'disabled'}>マスタ初期投入（得意先128 / 傭車先61）</button>
        <span class="ing-note" data-seednote>${online ? '正式名称・登録番号(T)入りの相手先マスタをSupabaseへ投入（初回のみ）' : 'Supabase接続時に有効'}</span></div>
      <div class="ing-seed"><button class="ing-btn ghost" data-dupcheck ${online ? '' : 'disabled'}>重複チェック</button>
        <span class="ing-note" data-dupresult>${online ? '取込後にここで整合を確認（同一管理ID＋内容一致の余剰＝重複を検出）' : 'Supabase接続時に有効'}</span></div>
    </div>
    <div class="ing-grid">${cards}${soon}</div>
    ${online ? `<div class="ing-mig">
      <h3>移行済担当（VEIL入力へ切替）<span class="k">切替日以降、その担当の日計表行は取込から除外（VEIL入力が正・二重計上防止）</span></h3>
      <div data-miglist><span class="ing-note">読み込み中…</span></div>
      <div class="ing-migadd">
        <input type="text" data-migstaff placeholder="担当名（例 近藤）">
        <input type="date" data-migdate>
        <button class="ing-btn ghost" data-migadd>追加/更新</button>
      </div>
    </div>` : ''}`

  el.querySelectorAll('input[data-file]').forEach((inp) => {
    inp.addEventListener('change', async (e) => {
      const vId = inp.dataset.file
      const f = e.target.files && e.target.files[0]
      if (!f) return
      const buf = await f.arrayBuffer()
      const text = decodeBytes(buf, VENDORS[vId].encoding)
      state[vId] = { fileName: f.name, text, plan: null }
      el.querySelector(`[data-fn="${vId}"]`).textContent = f.name
      await preview(vId, el, online, ctx)
    })
  })
  // 重複チェック（第2層バックストップ）
  const dupBtn = el.querySelector('[data-dupcheck]')
  if (dupBtn && online) dupBtn.addEventListener('click', () => checkDuplicates(el, dupBtn))
  // マスタ初期投入
  const seedBtn = el.querySelector('[data-seed]')
  if (seedBtn && online) seedBtn.addEventListener('click', async () => {
    seedBtn.disabled = true; seedBtn.textContent = '投入中…'
    try {
      const r = await seedMasters()
      const note = el.querySelector('[data-seednote]')
      if (note) note.textContent = `✓ 投入完了：得意先 ${r.customers} / 傭車先 ${r.partners}`
      seedBtn.textContent = 'マスタ投入済み'
    } catch (e) { seedBtn.textContent = 'マスタ初期投入'; seedBtn.disabled = false; alert('投入に失敗しました: ' + (e && e.message || e)) }
  })
  // 既存プランのボタンを再バインド
  VENDOR_LIST.forEach((v) => bindActions(v.id, el, online, ctx))
  if (online) initMig(el)
}

// 移行済担当（切替日）設定 — settings/migration の raw.staff = { 担当: 'YYYY-MM-DD' }
async function currentMig() {
  try { const m = await getOne('settings', 'migration'); return (m && m.staff) ? { ...m.staff } : {} } catch (e) { return {} }
}
async function renderMigList(el) {
  const box = el.querySelector('[data-miglist]'); if (!box) return
  const map = await currentMig()
  const keys = Object.keys(map).sort()
  box.innerHTML = keys.length
    ? keys.map((k) => `<div class="mrow"><b>${esc(k)}</b><span>切替日 ${esc(map[k])} 以降を取込除外</span><button class="ing-btn ghost" data-migdel="${esc(k)}" style="padding:3px 9px">削除</button></div>`).join('')
    : '<span class="ing-note">未登録（全担当が日計表運用）。担当がVEIL入力へ移ったら、担当名＋切替日を登録してください。</span>'
  box.querySelectorAll('[data-migdel]').forEach((b) => b.addEventListener('click', async () => {
    const cur = await currentMig(); delete cur[b.dataset.migdel]
    await upsert('settings', 'migration', { staff: cur }); await renderMigList(el)
  }))
}
async function initMig(el) {
  await renderMigList(el)
  const addBtn = el.querySelector('[data-migadd]')
  if (addBtn) addBtn.addEventListener('click', async () => {
    const staff = (el.querySelector('[data-migstaff]').value || '').trim()
    const date = (el.querySelector('[data-migdate]').value || '').trim()
    if (!staff || !date) { alert('担当名と切替日を入力してください'); return }
    const cur = await currentMig(); cur[staff] = date
    await upsert('settings', 'migration', { staff: cur })
    el.querySelector('[data-migstaff]').value = ''
    await renderMigList(el)
  })
}

function summaryBlock(plan, vId, online) {
  const canCommit = online && (plan.summary.new + plan.summary.changed) > 0 && !plan.committed
  const committedNote = plan.committed
    ? `<span class="ing-note">✓ 取込済み（更新 ${plan.written} 件・内容変更 ${plan.changed} 件）</span>` : ''
  return `<div class="ing-sum">${summaryHtml(plan)}</div>
    ${rowsTable(plan)}
    <div class="ing-actions">
      <button class="ing-btn primary" data-commit="${vId}" ${canCommit ? '' : 'disabled'}>確定取込</button>
      <button class="ing-btn ghost" data-clear="${vId}">クリア</button>
      ${committedNote}
      ${!online ? '<span class="ing-note">※確定取込はSupabase接続時のみ</span>' : ''}
    </div>`
}

async function preview(vId, el, online, ctx) {
  const s = state[vId]
  if (!s || !s.text) return
  const box = el.querySelector(`[data-preview="${vId}"]`)
  box.innerHTML = '<div class="ing-note">照合中…</div>'
  const ctx2 = await loadImportCtx(vId, online)
  try {
    s.plan = online
      ? await buildPlan({ vendorId: vId, text: s.text, ctx: ctx2 })
      : planImport({ vendorId: vId, text: s.text, existing: {}, ctx: ctx2 })
  } catch (err) {
    box.innerHTML = `<div class="ing-banner">取込エラー: ${esc(err && err.message || err)}</div>`
    return
  }
  box.innerHTML = summaryBlock(s.plan, vId, online)
  bindActions(vId, el, online, ctx)
}

function bindActions(vId, el, online, ctx) {
  const commitBtn = el.querySelector(`[data-commit="${vId}"]`)
  if (commitBtn) commitBtn.addEventListener('click', async () => {
    const s = state[vId]
    if (!s || !s.plan || !online) return
    commitBtn.disabled = true; commitBtn.textContent = '取込中…'
    try {
      const res = await commitPlan(s.plan, { fileName: s.fileName, user: ctx.user?.email || '' })
      s.plan = res
      // 反映後に再プレビュー（今度は全件が重複＝再取込安全の実証）
      await preview(vId, el, online, ctx)
      // 取込後の自動重複チェック（第2層バックストップ）
      const dcb = el.querySelector('[data-dupcheck]'); if (dcb && online) checkDuplicates(el, dcb)
    } catch (err) {
      commitBtn.textContent = '確定取込'; commitBtn.disabled = false
      alert('取込に失敗しました: ' + (err && err.message || err))
    }
  })
  const clearBtn = el.querySelector(`[data-clear="${vId}"]`)
  if (clearBtn) clearBtn.addEventListener('click', () => {
    delete state[vId]
    const box = el.querySelector(`[data-preview="${vId}"]`)
    if (box) box.innerHTML = ''
    const fn = el.querySelector(`[data-fn="${vId}"]`); if (fn) fn.textContent = ''
    const inp = el.querySelector(`input[data-file="${vId}"]`); if (inp) inp.value = ''
  })
}
